import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Alert,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import axios from 'axios';
import ExportButton from '../../utils/ExportButton';


// Auth helper function
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    },
    withCredentials: true
  };
};
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function ConsolidatedSummary({ user }) {
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  
  const [filters, setFilters] = useState({
    product: '',
    grade: '',
    brand: ''
  });

  const debouncedFilters = useDebounce(filters, 500);

  useEffect(() => {
    fetchData();
  }, [debouncedFilters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams(debouncedFilters);
      const response = await axios.get(
        `https://ccc4cs.choicegroup.in/api/consolidated_summary.php?${params}`,
        getAuthConfig()
      );

      if (response.data.success) {
        const processedData = (response.data.data || []).map(row => ({
          ...row,
          grade: String(row.grade || ''),
          pack_size: String(row.pack_size || '')
        }));
        
        setData(processedData);
        setTotals(response.data.totals || {});
      } else {
        setError('Failed to load consolidated summary');
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
      setError('Failed to load consolidated summary');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value
    });
  };

  const handleClearFilters = () => {
    setFilters({
      product: '',
      grade: '',
      brand: ''
    });
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(num || 0);
  };

  const displayGrade = (grade) => {
    if (!grade) return '-';
    // Remove leading apostrophe if present (Excel text format marker)
    const gradeStr = String(grade);
    return gradeStr.startsWith("'") ? gradeStr.substring(1) : gradeStr;
  };

  // Aggregate data by product only
  const getDataByProduct = () => {
    const grouped = {};
    data.forEach(row => {
      const key = row.product;
      if (!grouped[key]) {
        grouped[key] = {
          product: row.product,
          total_cartons: 0,
          total_loose_bags: 0,
          total_kg_loose: 0,
          total_weight_kgs: 0,
          total_weight_pounds: 0
        };
      }
      grouped[key].total_cartons += parseInt(row.total_cartons) || 0;
      grouped[key].total_loose_bags += parseInt(row.total_loose_bags) || 0;
      grouped[key].total_kg_loose += parseFloat(row.total_kg_loose) || 0;
      grouped[key].total_weight_kgs += parseFloat(row.total_weight_kgs) || 0;
      grouped[key].total_weight_pounds += parseFloat(row.total_weight_pounds) || 0;
    });
    return Object.values(grouped);
  };

  // Aggregate data by grade only
  const getDataByGrade = () => {
    const grouped = {};
    data.forEach(row => {
      const key = displayGrade(row.grade);
      if (!grouped[key]) {
        grouped[key] = {
          grade: key,
          total_cartons: 0,
          total_loose_bags: 0,
          total_kg_loose: 0,
          total_weight_kgs: 0,
          total_weight_pounds: 0
        };
      }
      grouped[key].total_cartons += parseInt(row.total_cartons) || 0;
      grouped[key].total_loose_bags += parseInt(row.total_loose_bags) || 0;
      grouped[key].total_kg_loose += parseFloat(row.total_kg_loose) || 0;
      grouped[key].total_weight_kgs += parseFloat(row.total_weight_kgs) || 0;
      grouped[key].total_weight_pounds += parseFloat(row.total_weight_pounds) || 0;
    });
    return Object.values(grouped);
  };

  // Aggregate data by brand only
  const getDataByBrand = () => {
    const grouped = {};
    data.forEach(row => {
      const key = row.brand;
      if (!grouped[key]) {
        grouped[key] = {
          brand: row.brand,
          total_cartons: 0,
          total_loose_bags: 0,
          total_kg_loose: 0,
          total_weight_kgs: 0,
          total_weight_pounds: 0
        };
      }
      grouped[key].total_cartons += parseInt(row.total_cartons) || 0;
      grouped[key].total_loose_bags += parseInt(row.total_loose_bags) || 0;
      grouped[key].total_kg_loose += parseFloat(row.total_kg_loose) || 0;
      grouped[key].total_weight_kgs += parseFloat(row.total_weight_kgs) || 0;
      grouped[key].total_weight_pounds += parseFloat(row.total_weight_pounds) || 0;
    });
    return Object.values(grouped);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            📊 Consolidated Summary
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Aggregate summary of Daily Inventory (PRODUCED, REPACK-IN, EXCESS-RETURNED)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ExportButton 
            exportType="consolidated_summary" 
            label="Export Summary"
            filters={filters}
          />
          <IconButton onClick={fetchData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3, boxShadow: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{ 
            borderBottom: 2, 
            borderColor: 'divider',
            bgcolor: '#f5f5f5',
            '& .MuiTab-root': {
              fontSize: '18px',
              fontWeight: 700,
              textTransform: 'none',
              minHeight: '64px',
              py: 2,
              px: 4,
              color: '#666',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: 'rgba(21, 101, 192, 0.08)',
                color: '#1565c0',
                transform: 'translateY(-2px)'
              },
              '&.Mui-selected': {
                color: '#1565c0',
                fontWeight: 800,
                bgcolor: 'white',
                borderRadius: '8px 8px 0 0',
                boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
              }
            },
            '& .MuiTabs-indicator': {
              height: 4,
              backgroundColor: '#1565c0',
              borderRadius: '4px 4px 0 0'
            }
          }}
        >
          <Tab label="📋 All Details" />
          <Tab label="📦 By Product" />
          <Tab label="⭐ By Grade" />
          <Tab label="🏷️ By Brand" />
        </Tabs>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Typography variant="body2">Total Cartons</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {totals.total_cartons || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ bgcolor: 'error.main', color: 'white' }}>
            <CardContent>
              <Typography variant="body2">Total Loose Bags</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {totals.total_loose_bags || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
            <CardContent>
              <Typography variant="body2">Total KG Loose</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {formatNumber(totals.total_kg_loose)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
            <CardContent>
              <Typography variant="body2">Total Weight (KGs)</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {formatNumber(totals.total_weight_kgs)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
            <CardContent>
              <Typography variant="body2">Total Weight (Lbs)</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {formatNumber(totals.total_weight_pounds)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Filters
          </Typography>
          {(filters.product || filters.grade || filters.brand) && (
            <IconButton 
              onClick={handleClearFilters} 
              size="small"
              sx={{ 
                bgcolor: 'grey.200',
                '&:hover': { bgcolor: 'grey.300' }
              }}
            >
              <ClearIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 0.5 }}>Clear All</Typography>
            </IconButton>
          )}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Product"
              value={filters.product}
              onChange={(e) => handleFilterChange('product', e.target.value)}
              placeholder="Type to filter..."
              InputProps={{
                endAdornment: filters.product && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleFilterChange('product', '')}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Grade"
              value={filters.grade}
              onChange={(e) => handleFilterChange('grade', e.target.value)}
              placeholder="Type to filter..."
              InputProps={{
                endAdornment: filters.grade && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleFilterChange('grade', '')}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Brand"
              value={filters.brand}
              onChange={(e) => handleFilterChange('brand', e.target.value)}
              placeholder="Type to filter..."
              InputProps={{
                endAdornment: filters.brand && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleFilterChange('brand', '')}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Tab 0: All Details */}
          {activeTab === 0 && (
            <TableContainer component={Paper} sx={{ border: '1px solid #e0e0e0' }}>
              <Table sx={{ '& td, & th': { border: '1px solid #e0e0e0' } }}>
                <TableHead sx={{ bgcolor: '#1565c0' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1565c0' }}>Product</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1565c0' }}>Grade</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1565c0' }}>Pack Size</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1565c0' }}>Brand</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1565c0' }}>Total Cartons</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1565c0' }}>Total Loose Bags</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1565c0' }}>Total KG Loose</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1565c0' }}>Total Weight (KGs)</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1565c0' }}>Total Weight (Pounds)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 5, border: '1px solid #e0e0e0' }}>
                        <Typography variant="body1" color="text.secondary">
                          No data found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row, index) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                          '&:hover': { bgcolor: 'rgba(21, 101, 192, 0.08)' }
                        }}
                      >
                        <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '14px' }}>
                          <strong>{row.product}</strong>
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '14px' }}>
                          <strong style={{ fontFamily: 'monospace', fontSize: '15px' }}>
                            {displayGrade(row.grade)}
                          </strong>
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '14px' }}>{row.pack_size}</TableCell>
                        <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '14px' }}>{row.brand}</TableCell>
                        <TableCell align="right" sx={{ border: '1px solid #e0e0e0', fontSize: '14px' }}>
                          <strong>{row.total_cartons}</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'error.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                          <strong>{row.total_loose_bags}</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'warning.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                          <strong>{formatNumber(row.total_kg_loose)}</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'success.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                          <strong>{formatNumber(row.total_weight_kgs)}</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'info.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                          <strong>{formatNumber(row.total_weight_pounds)}</strong>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Tab 1: By Product */}
          {activeTab === 1 && (
            <TableContainer component={Paper} sx={{ border: '1px solid #e0e0e0' }}>
              <Table sx={{ '& td, & th': { border: '1px solid #e0e0e0' } }}>
                <TableHead sx={{ bgcolor: '#2e7d32' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1b5e20' }}>Product</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1b5e20' }}>Total Cartons</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1b5e20' }}>Total Loose Bags</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1b5e20' }}>Total KG Loose</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1b5e20' }}>Total Weight (KGs)</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #1b5e20' }}>Total Weight (Pounds)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getDataByProduct().map((row, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                        '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.08)' }
                      }}
                    >
                      <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong style={{ fontSize: '16px' }}>{row.product}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{row.total_cartons}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{row.total_loose_bags}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'warning.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{formatNumber(row.total_kg_loose)}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'success.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{formatNumber(row.total_weight_kgs)}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'info.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{formatNumber(row.total_weight_pounds)}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Tab 2: By Grade */}
          {activeTab === 2 && (
            <TableContainer component={Paper} sx={{ border: '1px solid #e0e0e0' }}>
              <Table sx={{ '& td, & th': { border: '1px solid #e0e0e0' } }}>
                <TableHead sx={{ bgcolor: '#ed6c02' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #e65100' }}>Grade</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #e65100' }}>Total Cartons</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #e65100' }}>Total Loose Bags</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #e65100' }}>Total KG Loose</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #e65100' }}>Total Weight (KGs)</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #e65100' }}>Total Weight (Pounds)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getDataByGrade().map((row, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                        '&:hover': { bgcolor: 'rgba(237, 108, 2, 0.08)' }
                      }}
                    >
                      <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong style={{ fontFamily: 'monospace', fontSize: '16px' }}>{row.grade}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{row.total_cartons}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{row.total_loose_bags}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'warning.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{formatNumber(row.total_kg_loose)}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'success.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{formatNumber(row.total_weight_kgs)}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'info.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{formatNumber(row.total_weight_pounds)}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Tab 3: By Brand */}
          {activeTab === 3 && (
            <TableContainer component={Paper} sx={{ border: '1px solid #e0e0e0' }}>
              <Table sx={{ '& td, & th': { border: '1px solid #e0e0e0' } }}>
                <TableHead sx={{ bgcolor: '#9c27b0' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #7b1fa2' }}>Brand</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #7b1fa2' }}>Total Cartons</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #7b1fa2' }}>Total Loose Bags</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #7b1fa2' }}>Total KG Loose</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #7b1fa2' }}>Total Weight (KGs)</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '15px', border: '1px solid #7b1fa2' }}>Total Weight (Pounds)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getDataByBrand().map((row, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                        '&:hover': { bgcolor: 'rgba(156, 39, 176, 0.08)' }
                      }}
                    >
                      <TableCell sx={{ border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong style={{ fontSize: '16px' }}>{row.brand}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{row.total_cartons}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{row.total_loose_bags}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'warning.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{formatNumber(row.total_kg_loose)}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'success.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{formatNumber(row.total_weight_kgs)}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'info.main', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                        <strong>{formatNumber(row.total_weight_pounds)}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}

export default ConsolidatedSummary;